import { useState, useEffect } from 'react';
import { getVault, runBatch } from '../api';

const Icon = ({ type }) => {
  const icons = {
    usdc:    <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="14" cy="14" r="14" fill="#2775CA"/><text x="14" y="19" textAnchor="middle" fill="white" fontSize="13" fontWeight="700">$</text></svg>,
    btc:     <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="14" cy="14" r="14" fill="#FF9500"/><text x="14" y="19" textAnchor="middle" fill="white" fontSize="14" fontWeight="700">₿</text></svg>,
    pending: <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="14" cy="14" r="13" stroke="#AEAEB2" strokeWidth="2" fill="none"/><path d="M14 8v6l4 2" stroke="#AEAEB2" strokeWidth="2" strokeLinecap="round"/></svg>,
    spend:   <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="3" y="7" width="22" height="15" rx="3" stroke="#00C48C" strokeWidth="2" fill="none"/><path d="M3 12h22" stroke="#00C48C" strokeWidth="2"/><rect x="7" y="16" width="5" height="2" rx="1" fill="#00C48C"/></svg>,
    stack:   <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><polyline points="4,20 10,12 15,16 24,7" stroke="#FF9500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/><polyline points="19,7 24,7 24,12" stroke="#FF9500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>,
  };
  return icons[type] || null;
};

export default function VaultPage() {
  const [vault, setVault]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [batching, setBatching] = useState(false);
  const [msg, setMsg]           = useState('');
  const [msgType, setMsgType]   = useState('success');

  useEffect(() => {
    getVault().then(setVault).catch(console.error).finally(() => setLoading(false));
  }, []);

  async function handleBatch() {
    setBatching(true); setMsg('');
    try {
      const res = await runBatch();
      setMsgType('success');
      setMsg(res.skipped ? 'No pending saves — batch skipped' :
             res.error   ? 'Batch failed: '+res.error :
             `Batch complete — ${res.userCount} users, $${res.totalUsd} processed`);
      const updated = await getVault();
      setVault(updated);
    } catch (err) { setMsgType('error'); setMsg('Error: '+err.message); }
    finally { setBatching(false); }
  }

  function formatDate(ts) {
    return new Date(ts*1000).toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'});
  }

  if (loading) return <div style={{color:'#6E6E73',padding:20}}>Loading...</div>;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Vault & Reserves</h1>
          <p style={s.subtitle}>Platform balances and batch management</p>
        </div>
        <button style={s.batchBtn} onClick={handleBatch} disabled={batching}>
          {batching ? 'Running...' : '⚡ Execute Batch Now'}
        </button>
      </div>

      {msg && (
        <div style={{...s.alert, ...(msgType==='error' ? s.alertError : s.alertSuccess)}}>
          {msg}
        </div>
      )}

      {/* CDP Treasury */}
      <h2 style={s.sectionTitle}>CDP Treasury</h2>
      <div style={s.grid}>
        <div style={s.vaultCard}>
          <div style={s.iconWrap}><Icon type="usdc" /></div>
          <div style={s.vaultLabel}>USDC Balance</div>
          <div style={s.vaultValue}>${vault?.cdpTreasury?.usdcBalance||'0.00'}</div>
          <div style={s.vaultSub}>Available for payouts</div>
        </div>
        <div style={s.vaultCard}>
          <div style={s.iconWrap}><Icon type="btc" /></div>
          <div style={s.vaultLabel}>cbBTC Balance</div>
          <div style={{...s.vaultValue,color:'#FF9500'}}>{vault?.cdpTreasury?.cbbtcBalance||'0.00000000'}</div>
          <div style={s.vaultSub}>{(vault?.cdpTreasury?.satoshis||0).toLocaleString()} satoshis</div>
        </div>
        <div style={s.vaultCard}>
          <div style={s.iconWrap}><Icon type="pending" /></div>
          <div style={s.vaultLabel}>Pending BTC Queue</div>
          <div style={{...s.vaultValue,color:'#AEAEB2'}}>${vault?.pendingBtc?.pendingUsd||'0.00'}</div>
          <div style={s.vaultSub}>Awaiting next batch</div>
        </div>
      </div>

      {/* USDC Float Yield */}
      <h2 style={s.sectionTitle}>USDC Float Yield</h2>
      <div style={{...s.grid, gridTemplateColumns:'repeat(3,1fr)'}}>
        {(() => {
          const usdc = parseFloat(vault?.cdpTreasury?.usdcBalance || 0);
          const apy  = 4.5;
          const daily   = (usdc * apy / 100 / 365).toFixed(4);
          const monthly = (usdc * apy / 100 / 12).toFixed(2);
          const annual  = (usdc * apy / 100).toFixed(2);
          return (<>
            <div style={s.vaultCard}>
              <div style={s.iconWrap}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <circle cx="14" cy="14" r="13" stroke="#00C48C" strokeWidth="2" fill="none"/>
                  <path d="M9 14h10M14 9v10" stroke="#00C48C" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={s.vaultLabel}>Daily Yield (est.)</div>
              <div style={{...s.vaultValue, color:'#00C48C'}}>${daily}</div>
              <div style={s.vaultSub}>@ {apy}% APY on ${usdc.toFixed(2)} USDC</div>
            </div>
            <div style={s.vaultCard}>
              <div style={s.iconWrap}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <circle cx="14" cy="14" r="13" stroke="#00C48C" strokeWidth="2" fill="none"/>
                  <path d="M9 14h10M14 9v10" stroke="#00C48C" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={s.vaultLabel}>Monthly Yield (est.)</div>
              <div style={{...s.vaultValue, color:'#00C48C'}}>${monthly}</div>
              <div style={s.vaultSub}>@ {apy}% APY on ${usdc.toFixed(2)} USDC</div>
            </div>
            <div style={s.vaultCard}>
              <div style={s.iconWrap}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <circle cx="14" cy="14" r="13" stroke="#00C48C" strokeWidth="2" fill="none"/>
                  <path d="M9 14h10M14 9v10" stroke="#00C48C" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={s.vaultLabel}>Annual Yield (est.)</div>
              <div style={{...s.vaultValue, color:'#00C48C'}}>${annual}</div>
              <div style={s.vaultSub}>@ {apy}% APY on ${usdc.toFixed(2)} USDC</div>
            </div>
          </>);
        })()}
      </div>
      <div style={{background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:12, padding:'10px 16px', marginBottom:32, fontSize:12, color:'#B87A00'}}>
        ⚠️ Yield figures are estimates based on 4.5% APY. Once Coinbase Business account is verified, update with real yield data from Coinbase Business API.
      </div>

      {/* Platform Totals */}
      <h2 style={s.sectionTitle}>Platform Totals</h2>
      <div style={{...s.grid, gridTemplateColumns:'repeat(2,1fr)'}}>
        <div style={s.vaultCard}>
          <div style={s.iconWrap}><Icon type="spend" /></div>
          <div style={s.vaultLabel}>Total Spend Balances</div>
          <div style={{...s.vaultValue,color:'#00C48C'}}>${vault?.platformTotals?.spendUsd||'0.00'}</div>
          <div style={s.vaultSub}>Across all users</div>
        </div>
        <div style={s.vaultCard}>
          <div style={s.iconWrap}><Icon type="stack" /></div>
          <div style={s.vaultLabel}>Total Stack Balances</div>
          <div style={{...s.vaultValue,color:'#FF9500'}}>${vault?.platformTotals?.stackUsd||'0.00'}</div>
          <div style={s.vaultSub}>{(vault?.platformTotals?.satoshis||0).toLocaleString()} satoshis</div>
        </div>
      </div>

      {/* User Balances */}
      {vault?.userBalances?.length > 0 && (
        <>
          <h2 style={s.sectionTitle}>User Balances</h2>
          <div style={s.tableWrap}>
            <div style={s.tableHeader}>
              <span style={{flex:2}}>User</span>
              <span style={{flex:1}}>Spend</span>
              <span style={{flex:1}}>Stack (USD)</span>
              <span style={{flex:1}}>Satoshis</span>
            </div>
            {vault.userBalances.map(u => (
              <div key={u.id} style={s.tableRow}>
                <span style={{flex:2}}>
                  <div style={{fontSize:14,fontWeight:600,color:'#1D1D1F'}}>{u.name}</div>
                  <div style={{fontSize:12,color:'#6E6E73'}}>{u.email}</div>
                </span>
                <span style={{flex:1,fontSize:15,fontWeight:600,color:'#00C48C'}}>${u.spendUsd}</span>
                <span style={{flex:1,fontSize:15,fontWeight:600,color:'#FF9500'}}>${u.stackUsd}</span>
                <span style={{flex:1,fontSize:13,color:'#6E6E73'}}>{u.satoshis.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Recent Batches */}
      {vault?.recentBatches?.length > 0 && (
        <>
          <h2 style={s.sectionTitle}>Recent Batches</h2>
          <div style={s.tableWrap}>
            <div style={s.tableHeader}>
              <span style={{flex:1}}>Status</span>
              <span style={{flex:1}}>USD Total</span>
              <span style={{flex:1}}>BTC Price</span>
              <span style={{flex:2}}>TX Hash</span>
              <span style={{flex:1.5}}>Date</span>
            </div>
            {vault.recentBatches.map(b => (
              <div key={b.id} style={s.tableRow}>
                <span style={{flex:1}}>
                  <span style={{fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:6,
                    background:b.status==='completed'?'#E6FAF5':'#FFF0EF',
                    color:b.status==='completed'?'#00C48C':'#FF3B30'}}>
                    {b.status}
                  </span>
                </span>
                <span style={{flex:1,fontSize:13,fontWeight:500}}>${(b.total_usd_cents/100).toFixed(2)}</span>
                <span style={{flex:1,fontSize:13}}>{b.btc_price_cents?'$'+(b.btc_price_cents/100).toLocaleString():'—'}</span>
                <span style={{flex:2}}>
                  {b.tx_hash && b.tx_hash !== 'internal' ? (
                    <a href={`https://basescan.org/tx/${b.tx_hash}`} target="_blank" rel="noreferrer" style={s.link}>
                      {b.tx_hash.slice(0,18)}...
                    </a>
                  ) : b.tx_hash === 'internal' ? <span style={{color:'#AEAEB2',fontSize:12}}>Internal</span> : '—'}
                </span>
                <span style={{flex:1.5,fontSize:12,color:'#6E6E73'}}>{formatDate(b.created_at)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  header:       {display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24},
  title:        {fontSize:24,fontWeight:700,color:'#1D1D1F'},
  subtitle:     {fontSize:14,color:'#6E6E73',marginTop:4},
  batchBtn:     {background:'#007AFF',color:'#fff',border:'none',padding:'12px 20px',borderRadius:12,fontSize:14,fontWeight:600,cursor:'pointer',boxShadow:'0 4px 12px rgba(0,122,255,0.3)'},
  alert:        {borderRadius:12,padding:'12px 16px',marginBottom:20,fontSize:14,fontWeight:500},
  alertSuccess: {background:'#E6FAF5',border:'1px solid #B3F0DE',color:'#00A86B'},
  alertError:   {background:'#FFF0EF',border:'1px solid #FFD0CE',color:'#FF3B30'},
  grid:         {display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:20,marginBottom:32},
  vaultCard:    {background:'#fff',borderRadius:20,padding:24,boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  iconWrap:     {marginBottom:16},
  vaultLabel:   {fontSize:13,color:'#6E6E73',marginBottom:8,fontWeight:500},
  vaultValue:   {fontSize:30,fontWeight:700,marginBottom:6,color:'#1D1D1F'},
  vaultSub:     {fontSize:12,color:'#AEAEB2'},
  link:         {fontSize:13,color:'#007AFF',textDecoration:'none'},
  sectionTitle: {fontSize:18,fontWeight:600,marginBottom:12,marginTop:8,color:'#1D1D1F'},
  tableWrap:    {background:'#fff',borderRadius:16,overflow:'hidden',boxShadow:'0 2px 20px rgba(0,0,0,0.06)',marginBottom:32},
  tableHeader:  {display:'flex',padding:'12px 20px',background:'#F5F5F7',fontSize:11,fontWeight:600,color:'#6E6E73',textTransform:'uppercase',letterSpacing:0.5},
  tableRow:     {display:'flex',padding:'14px 20px',borderBottom:'1px solid #F5F5F7',alignItems:'center'}
};
